import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Mail } from 'lucide-react'
import { getSupabaseClient } from '@/infrastructure/supabase/app'
import { Card } from '@/presentation/components/ui/Card'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'

export function ForgotPasswordPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    try {
      const client = getSupabaseClient()
      const { error: resetError } = await client.auth.resetPasswordForEmail(email, {
        redirectTo: window.location.origin + '/#/reset-password',
      })

      if (resetError) throw resetError
      setSuccess(true)
    } catch (err) {
      console.error('Error al solicitar restablecimiento de contrasena:', err)
      setError(err instanceof Error ? err.message : 'No se pudo enviar el correo de recuperacion.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="app-shell flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-modal">
        <div className="text-center mb-6">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-[14px] bg-gradient-to-br from-primary-500 to-[#8e67ff] text-2xl font-bold text-white shadow-glow">
            Z
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">Restablecer contrasena</h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            Ingresa tu correo y te enviaremos un enlace de recuperacion.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700 dark:border-danger-900/70 dark:bg-danger-950/40 dark:text-danger-200">
            {error}
          </div>
        )}

        {success ? (
          <div className="space-y-4 text-center">
            <div className="rounded-md border border-success-200 bg-success-50 px-3 py-2 text-sm text-success-700 dark:border-success-900/70 dark:bg-success-950/40 dark:text-success-200">
              Hemos enviado un correo electronico con las instrucciones para restablecer tu contrasena. Por favor revisa tu bandeja de entrada.
            </div>
            <Button
              fullWidth
              variant="outline"
              leftIcon={<ArrowLeft className="w-4 h-4" />}
              onClick={() => navigate('/login')}
            >
              Volver al inicio de sesion
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              label="Correo electronico"
              type="email"
              placeholder="correo@ejemplo.com"
              leftIcon={<Mail className="w-4 h-4 text-neutral-400" />}
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />

            <Button
              fullWidth
              variant="primary"
              size="lg"
              type="submit"
              disabled={isLoading || !email}
            >
              {isLoading ? 'Enviando...' : 'Enviar enlace'}
            </Button>

            <button
              type="button"
              className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 mt-2 transition-colors focus:outline-none"
              onClick={() => navigate('/login')}
              disabled={isLoading}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Volver al inicio de sesion
            </button>
          </form>
        )}
      </Card>
    </div>
  )
}
